import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { logError } from 'util/logging';
import config from 'config';

type State = { state: 'fetching' } | { state: 'unauthenticated' } | { state: 'authenticated'; accessToken: string };

// use this hook if you want to do authenticated api calls
// outside of <AuthenticatedRoutes />

export default function useAccessToken() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [accessToken, setToken] = useState<State>({ state: 'fetching' });
  useEffect(() => {
    (async () => {
      if (isLoading) {
        setToken({ state: 'fetching' });
        return;
      }
      if (!isAuthenticated) {
        setToken({ state: 'unauthenticated' });
        return;
      }
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: config.AUTH0_API_IDENTIFIER,
          },
        });
        setToken({ state: 'authenticated', accessToken: token });
      } catch (error) {
        // If we can't get a token (expired, network error, etc), treat as unauthenticated
        // to avoid sending expired/invalid tokens
        const errorType = error?.error || 'unknown';
        const errorDescription = error?.error_description || error?.message || 'No description';
        const pathname = typeof window !== 'undefined' ? window.location.pathname : 'unknown';

        logError(error, 'Failed to get access token', {
          errorType,
          errorDescription,
          pathname,
          isAuthenticated,
          isLoading,
        });

        setToken({ state: 'unauthenticated' });
      }
    })();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return [accessToken];
}

export function getAccessTokenOrUndefined(accessToken: State) {
  return accessToken.state === 'authenticated' ? accessToken.accessToken : undefined;
}
